import createGame from './client/miserit-anthar.js';
import { disposeHierarchy } from './client/miserit-anthar.js';
import { buildBoxTree, patchBoxTree } from './client/box-patcher.js';
import { initLcarsHud, prewarmHudFonts } from './client/hud-lcars.js';
import { splashDone } from './client/instances.js';
import { SectorMap } from './client/sector-map.js';
import { planetLighting, setPlanetNightFillIntensity } from './client/sun-shader.js';
import { loadUnitDesigns } from './client/unit.js';
import Entities from './hybrid/entities.js';
import { xrSettings } from './client/xr-settings.js';
import * as focus from './client/focus.js';

// Views are NOT bundled here: the canvas adapter imports a route's `views/<component>.js` on
// demand (served raw, cached by the browser) and hands the renderer its default export, which is
// evaluated with the live controller on every $rerender (2026-10-11, user: "the view just contains
// conditional trees and gets cached by the browser"; the 10-10 static imports were a deviation).
// The shell (game/app) has NO view: the LCARS frame (client/hud-lcars.js) is the interface.

// an SSR entry on /x/y opens the map at that sector (user 2026-10-10); SPA jumps go through
// frontend/home.js ($afterLoad does not run on the Canvas first load)
function entrySector() {
	const m = location.pathname.match(/^\/(-?\d+)\/(-?\d+)(?:\/|$)/);
	return m ? { x: Number(m[1]), y: Number(m[2]) } : { x: 0, y: 0 };
}

// where a built level goes: level 0 (the game/app shell) → game.gui; a routed page (level ≥ 1)
// → the LCARS frame's page slot (client/hud-panels.js) — or game.gui until the frame exists
// (cold load of /settings: the frame's fonts are still loading; createHudPanels adopts it)
function mountLevel(container, chainIndex) {
	if (chainIndex >= 1 && game.hudPanels?.setPage) { game.hudPanels.setPage(container); return; }
	if (container.parent !== game.gui) game.gui.add(container);
}

// navigationUp is a one-shot event with no replay (see hybrid/observer.js): if boot
// navigation already fired it before this listener attaches, the event is lost and the
// splash hangs forever. Resolve immediately when navigation has already completed.
const navigationReady = navigation.current
	? Promise.resolve()
	: new Promise(resolve => observer.connect('navigationUp', resolve));

prewarmHudFonts();
globalThis.game = await createGame();
game.xrSettings = xrSettings; // frontend/settings.js (served raw, no bundle imports) reads the live store here
game.focus = focus; // camera focus for planet / settlement links (frontend/planet.js, frontend/settlement.js)
// server push of a settlement's production queue (server/build-queue.js) → the open settlement page
// population tick (server/population.js) → the open settlement page's numbers
receiver.settlementUpdate = ({ hostId, population, resources, rates, tickedAt }) => {
	const c = navigation.current;
	if (c && c.settlement && String(c.settlement._id) === String(hostId) && c.$setState) {
		c.$setState({ settlement: { ...c.settlement, population: Math.round(population.total), buildingMaterials: Math.round(resources.buildingMaterials || 0), food: Math.round(resources.food || 0), tickedAt: tickedAt ?? c.settlement.tickedAt }, ...(rates && c.rates ? { rates } : {}) });
	}
};
receiver.buildQueue = ({ hostId, pending, facilities }) => {
	const c = navigation.current;
	if (c && c.settlement && String(c.settlement._id) === String(hostId) && c.$setState) c.$setState({ pending, ...(facilities ? { facilities } : {}) });
};
await loadUnitDesigns();
game.disposeHierarchy = disposeHierarchy;
game.planetLighting = planetLighting;
game.setPlanetNightFillIntensity = setPlanetNightFillIntensity;
globalThis.setPlanetNightFillIntensity = setPlanetNightFillIntensity;
game.setSpaceBackground();
game.entities = new Entities();
game.sectorMap = new SectorMap(game);
if (navigator.xr && await navigator.xr.isSessionSupported('immersive-vr')) {
	// Lazy: web-xr + its three XR addons are fetched only here, for headset visitors.
	const { default: initWebXR } = await import('xr');
	globalThis.xr = initWebXR(game);
	game.onFrame(xr.update); // XR raycaster + controls before sectorMap uses them
}
game.onFrame(() => game.sectorMap.update());
initLcarsHud().catch(e => console.error('[lcars-hud] init failed', e));
const gridReady = webSocket.isSocketConnected
	? game.sectorMap.jumpTo(entrySector())
	: new Promise(resolve => observer.connect('webSocketClient.reconnect',
		() => game.sectorMap.jumpTo(entrySector()).then(resolve)));

// splash ends when the sector grid and the navigation are up (no timer)
Promise.all([gridReady, navigationReady]).then(splashDone);
// SSR entry on a planet / settlement link: focus the camera once the sector's planets exist
gridReady.then(() => focus.focusFromUrl()).catch(e => console.error('[focus]', e));

globalThis.$renderer = {
	_levels: [], // GUI containers per chain level
	_levelState: [],

	async init(data, ssr) {
	},

	async _renderLevelView(state) {
		const view = state.response?.view ?? state.controller.$view;
		return typeof view === 'function' ? view(state.controller) : view;
	},

	_installStateApi(controller, response, chainIndex) {
		const level = response?.chain?.[chainIndex] || null;
		this._levelState[chainIndex] = { controller, response, level };
		controller.$rerender = async () => {
			const state = this._levelState[chainIndex];
			if (!state) return;
			const view = await this._renderLevelView(state);
			if (!view) return;
			const previous = this._levels[chainIndex];
			const container = previous
				? await patchBoxTree(previous, view, state.controller)
				: await buildBoxTree(view, state.controller);
			this._levels[chainIndex] = container;
			mountLevel(container, chainIndex);
		};
		controller.$setState = async patch => {
			Object.assign(controller, typeof patch === 'function' ? patch(controller) : patch);
			await controller.$rerender();
		};
		controller.$replaceState = async next => {
			for (const key of Object.keys(controller)) {
				if (!key.startsWith('$')) delete controller[key];
			}
			Object.assign(controller, next);
			await controller.$rerender();
		};
	},

	async setScene(controller, response, chainIndex = 0) {
		this._installStateApi(controller, response, chainIndex);
		const view = await this._renderLevelView(this._levelState[chainIndex]);
		if (view) {
			const previous = this._levels[chainIndex];
			const container = previous
				? await patchBoxTree(previous, view, controller)
				: await buildBoxTree(view, controller);
			this._levels[chainIndex] = container;
			mountLevel(container, chainIndex);
		}
	},

	removeScene(chainIndex) {
		const container = this._levels[chainIndex];
		if (container) {
			if (game.hudPanels?.page?.() === container) game.hudPanels.setPage(null);
			// dispose() frees geometries/materials/text meshes AND unregisters interaction
			// targets (and detaches from game.gui). Without it every scene change leaked.
			container.dispose?.();
			game.gui.remove(container);
			this._levels[chainIndex] = null;
			this._levelState[chainIndex] = null;
		}
	},

	clearScenes() {
		while (game.gui.children.length) {
			const child = game.gui.children[0];
			child.dispose?.();
			game.gui.remove(child);
		}
		this._levels = [];
		this._levelState = [];
	},

	restart() {
	},

	leave(nextController) {
		this.clearScenes();
	},
};
